<script setup lang="ts">
definePageMeta({ layout: 'login' });

const { switchRole, defaultRoute } = useDemoMode();
const router = useRouter();
const loading = ref<string | null>(null);

interface RoleCard {
    userId: string;
    role: string;
    name: string;
    title: string;
    description: string;
    color: string;
    icon: string;
    initials: string;
}

const roleCards: RoleCard[] = [
    {
        userId: '00000000-0000-0000-0000-000000000a01',
        role: 'admin',
        name: 'Mike Reynolds',
        title: 'Administrator',
        description: 'Full access. Configure clients, facilities, users, and system settings.',
        color: 'blue',
        icon: 'i-lucide-shield-check',
        initials: 'MR'
    },
    {
        userId: '00000000-0000-0000-0000-000000000d01',
        role: 'dispatcher',
        name: 'Sarah Nguyen',
        title: 'Dispatcher',
        description: 'Manage orders. Assign techs. Monitor status. The war room.',
        color: 'teal',
        icon: 'i-lucide-radio',
        initials: 'SN'
    },
    {
        userId: '00000000-0000-0000-0000-000000000t01',
        role: 'technician',
        name: 'Maria Garcia',
        title: 'Technician',
        description: 'See your route. Work your queue. Tap through your day.',
        color: 'emerald',
        icon: 'i-lucide-stethoscope',
        initials: 'MG'
    },
    {
        userId: '00000000-0000-0000-0000-000000000r01',
        role: 'client_csr',
        name: 'Patricia Lee',
        title: 'Client CSR',
        description: 'Submit orders. Track status. Manage your facility\'s requests.',
        color: 'amber',
        icon: 'i-lucide-headset',
        initials: 'PL'
    },
    {
        userId: '00000000-0000-0000-0000-000000000s01',
        role: 'super_client',
        name: 'Robert Chan',
        title: 'Super-Client',
        description: 'Supervise techs. View maps. Monitor your organization\'s service.',
        color: 'violet',
        icon: 'i-lucide-binoculars',
        initials: 'RC'
    }
];

async function selectRole(card: RoleCard) {
    loading.value = card.userId;
    try {
        await switchRole(card.userId);
        await router.push(defaultRoute.value);
    } catch (e) {
        console.error('Failed to switch role:', e);
    } finally {
        loading.value = null;
    }
}
</script>

<template>
    <div class="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 flex flex-col items-center justify-center p-4 sm:p-8">
        <!-- Header -->
        <div class="text-center mb-10">
            <div class="flex items-center justify-center gap-3 mb-4">
                <UIcon name="i-lucide-truck" class="w-10 h-10 text-teal-400" />
                <h1 class="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                    Dependable Dispatch
                </h1>
            </div>
            <p class="text-slate-400 text-lg">
                Select a role to explore the demo
            </p>
        </div>

        <!-- Role Cards Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl w-full">
            <UCard
                v-for="card in roleCards"
                :key="card.userId"
                class="cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-xl border border-slate-700/50 hover:border-slate-600"
                :class="{ 'opacity-50 pointer-events-none': loading && loading !== card.userId }"
                @click="selectRole(card)"
            >
                <div class="flex items-start gap-4">
                    <!-- Avatar -->
                    <div
                        class="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0"
                        :class="{
                            'bg-blue-600': card.color === 'blue',
                            'bg-teal-600': card.color === 'teal',
                            'bg-emerald-600': card.color === 'emerald',
                            'bg-amber-600': card.color === 'amber',
                            'bg-violet-600': card.color === 'violet'
                        }"
                    >
                        {{ card.initials }}
                    </div>

                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-1">
                            <UIcon :name="card.icon" class="w-4 h-4 text-slate-400" />
                            <UBadge
                                :color="card.color as any"
                                variant="subtle"
                                size="xs"
                            >
                                {{ card.title }}
                            </UBadge>
                        </div>
                        <p class="font-semibold text-white text-sm">{{ card.name }}</p>
                        <p class="text-xs text-slate-400 mt-1">{{ card.description }}</p>
                    </div>

                    <!-- Loading indicator -->
                    <UIcon
                        v-if="loading === card.userId"
                        name="i-lucide-loader-2"
                        class="w-5 h-5 animate-spin text-teal-400 shrink-0"
                    />
                    <UIcon
                        v-else
                        name="i-lucide-arrow-right"
                        class="w-5 h-5 text-slate-600 shrink-0"
                    />
                </div>
            </UCard>
        </div>

        <!-- Footer -->
        <div class="mt-10 text-center">
            <p class="text-slate-500 text-xs">
                Demo mode &mdash; all data is in-memory and resets on reload
            </p>
        </div>
    </div>
</template>
